import { tenantDetails } from './types';

export interface EquifaxValidationResult {
  isValid: boolean;
  errorType?: 'idv_required' | 'profile_incomplete';
  errorMessage?: string;
}

/**
 * Validates whether a tenant meets all prerequisites for subscribing to Equifax Credit Reporting:
 * 1. IDV must be approved (isIdentityVerified === 1 or tenantIdv.status === 'approved').
 * 2. Required user details (firstName, lastName, dateOfBirth) must be present.
 */
export function validateEquifaxSubscriptionEligibility(
  tenant?: tenantDetails | null
): EquifaxValidationResult {
  if (!tenant) {
    return {
      isValid: false,
      errorType: 'idv_required',
      errorMessage: 'Tenant details not loaded. Please try again or complete Identity Verification.',
    };
  }

  // 1. Check IDV status
  const isIdvApproved =
    tenant.tenant?.isIdentityVerified === 1 ||
    (tenant.tenant?.isIdentityVerified as unknown) === true ||
    tenant.tenant?.tenantIdv?.status === 'approved';

  if (!isIdvApproved) {
    return {
      isValid: false,
      errorType: 'idv_required',
      errorMessage: 'Please complete Identity Verification (IDV) before subscribing to Equifax Credit Reporting.',
    };
  }

  // 2. Check user required details (firstName, lastName, dateOfBirth)
  const firstName = tenant.user?.firstName?.trim();
  const lastName = tenant.user?.lastName?.trim();
  const dateOfBirth = tenant.user?.dateOfBirth?.trim();

  const missingFields: string[] = [];
  if (!firstName) missingFields.push('First Name');
  if (!lastName) missingFields.push('Last Name');
  if (!dateOfBirth) missingFields.push('Date of Birth');

  if (missingFields.length > 0) {
    return {
      isValid: false,
      errorType: 'profile_incomplete',
      errorMessage: `Your account is missing required information (${missingFields.join(', ')}) needed for Equifax Credit Reporting. Please contact support to update your profile.`,
    };
  }

  return { isValid: true };
}
